import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type SettingRow = {
  key: string
  label: string
  help: string
  value: string
  options?: string[]
}

export default function AdminSettings({ settings }: { settings: SettingRow[] }) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm<Record<string, string>>(
    Object.fromEntries(settings.map((s) => [s.key, s.value])),
  )

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ settings: data }))
    form.patch("/admin/settings")
  }

  return (
    <>
      <Head title="Settings">
        <meta name="description" content="Manage FullCast site settings." />
        <meta property="og:title" content="Settings" />
        <meta property="og:description" content="Manage FullCast site settings." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Settings</h1>
          <p className="mt-2 text-ink-muted">
            Operational settings, editable without a deploy. Email settings take
            effect on the next message sent; the audit model on the next audit.
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 max-w-xl space-y-6">
          {settings.map((s) => (
            <div key={s.key} className="space-y-2">
              <label htmlFor={s.key}>{s.label}</label>
              {s.options ? (
                <Select
                  id={s.key}
                  value={form.data[s.key] ?? ""}
                  onChange={(e) => form.setData(s.key, e.target.value)}
                >
                  {s.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  id={s.key}
                  value={form.data[s.key] ?? ""}
                  onChange={(e) => form.setData(s.key, e.target.value)}
                />
              )}
              <p className="text-xs text-ink-muted">{s.help}</p>
              {errors[s.key] && (
                <p className="text-xs text-danger-display">{errors[s.key]}</p>
              )}
            </div>
          ))}

          <div className="flex items-center gap-3 border-t border-hairline pt-6">
            <Button type="submit" disabled={form.processing}>
              {form.processing ? "Saving…" : "Save settings"}
            </Button>
            {form.recentlySuccessful && (
              <span className="text-sm text-ink-muted">Saved ✓</span>
            )}
          </div>
        </form>
      </AdminShell>
    </>
  )
}
